/* The native Klaviyo embed owns all step submissions and consent.
 * A dialog contains the native embed or the existing Shopify fallback.
 * Move the fallback, never clone it or intercept its submission.
 */
(() => {
  const connect = (section) => {
    if (section.dataset.communityConnected) return;
    const dialog = section.querySelector('[data-community-dialog]');
    const opener = section.querySelector('[data-community-open]');
    const close = section.querySelector('[data-community-close]');
    const fallback = section.querySelector('[data-community-fallback]');
    const embed = section.querySelector('[data-community-embed]');
    const story = section.querySelector('[data-community-popup-story]');
    const automatic = section.hasAttribute('data-community-auto');
    if (!dialog?.showModal || !opener || !close || !fallback) return;
    section.dataset.communityConnected = 'true';
    dialog.append(fallback);
    if (!automatic) opener.hidden = false;
    let fallbackInUse = false;
    let formEngaged = false;

    const update = () => {
      const form = embed?.querySelector('form');
      const usable = Boolean(form && form.querySelector('button, input'));
      fallback.hidden = usable && !fallbackInUse;
      if (embed) embed.hidden = fallbackInUse;
      if (story) {
        const fields = form?.querySelectorAll('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"])') || [];
        const fieldVisible = Array.from(fields).some((field) => field.getBoundingClientRect().height > 0);
        if (fieldVisible) formEngaged = true;
        story.hidden = !usable || fallbackInUse || formEngaged;
      }
      // The same language form serves its original country and Switzerland.
      // Keep privacy links in the visitor's actual regional directory.
      const localeRoot = window.location.pathname.match(/^\/(?:en-gb|es-es|de-de|fr-ch|de-ch|en-ch)(?=\/|$)/)?.[0] || '';
      embed?.querySelectorAll('a[href]').forEach((link) => {
        try {
          const destination = new URL(link.getAttribute('href'), window.location.origin);
          const path = destination.pathname.replace(/^\/(?:en-gb|es-es|de-de|fr-ch|de-ch|en-ch)(?=\/|$)/, '');
          if (destination.origin !== window.location.origin || path !== '/pages/politique-confidentialite') return;
          const localized = `${localeRoot}${path}${destination.search}${destination.hash}`;
          if (link.getAttribute('href') !== localized) link.setAttribute('href', localized);
        } catch { /* Ignore non-web contact links. */ }
      });
    };
    const keepFallback = () => {
      fallbackInUse = true;
      update();
    };
    ['focusin', 'input', 'submit'].forEach((eventName) => {
      fallback.addEventListener(eventName, keepFallback, { once: true });
    });

    let observer;
    if (embed) {
      observer = new MutationObserver(update);
      observer.observe(embed, { childList: true, subtree: true });
    }
    update();

    let previousFocus;
    const open = () => {
      if (dialog.open) return;
      previousFocus = document.activeElement;
      dialog.showModal();
      update();
    };
    opener.addEventListener('click', open);
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => {
      const focusTarget = automatic ? previousFocus : opener;
      if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
    });
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    });
    let autoTimer;
    if (automatic && !window.Shopify?.designMode) {
      const seenCookie = 'adepsia_popup_seen';
      const subscribedCookie = 'adepsia_subscribed';
      const hasCookie = (name) => document.cookie.split('; ').some((entry) => entry.startsWith(`${name}=`));
      const remember = (name, days) => {
        const expiry = new Date(Date.now() + days * 86400000).toUTCString();
        document.cookie = `${name}=1; expires=${expiry}; path=/; SameSite=Lax`;
      };
      const success = fallback.querySelector('[data-community-success]');
      const delay = Math.max(0, Number(section.dataset.delay) || 0);
      const seenDays = Number(section.dataset.seenDays) || 7;
      const tryOpen = () => {
        if (!section.isConnected || hasCookie(seenCookie) || hasCookie(subscribedCookie)) return;
        const anotherDialog = [...document.querySelectorAll('dialog[open], [aria-modal="true"]')].some((element) => element !== dialog && element.getBoundingClientRect().height > 0 && getComputedStyle(element).visibility !== 'hidden');
        const consentBanner = document.querySelector('#shopify-pc__banner');
        const consentVisible = Boolean(consentBanner && consentBanner.getBoundingClientRect().height > 0 && getComputedStyle(consentBanner).visibility !== 'hidden');
        if (anotherDialog || consentVisible) {
          autoTimer = window.setTimeout(tryOpen, 1000);
          return;
        }
        remember(seenCookie, seenDays);
        open();
      };
      if (success) {
        remember(subscribedCookie, 365);
        remember(seenCookie, 365);
        open();
      } else if (!hasCookie(seenCookie) && !hasCookie(subscribedCookie)) {
        autoTimer = window.setTimeout(tryOpen, delay);
      }
    }
    section.addEventListener('adepsia:community:disconnect', () => {
      window.clearTimeout(autoTimer);
      observer?.disconnect();
      if (dialog.open) dialog.close();
    }, { once: true });
  };

  const initialize = (root) => {
    if (root.matches?.('[data-community-signup]')) connect(root);
    root.querySelectorAll('[data-community-signup]').forEach(connect);
  };
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target;
    if (root.matches?.('[data-community-signup]')) root.dispatchEvent(new Event('adepsia:community:disconnect'));
    root.querySelectorAll('[data-community-signup]').forEach((section) => section.dispatchEvent(new Event('adepsia:community:disconnect')));
  });
})();

/* International native forms: explicitly save optional contact data on the
 * submitted email profile. The native embed still owns email subscription.
 * This uses the public Client Profiles endpoint, never a private API key or
 * a SMS/WhatsApp subscription endpoint. No message is sent.
 */
(() => {
  if (window.adepsiaInternationalProfileCapture) return;
  window.adepsiaInternationalProfileCapture = true;
  const locales = { T6X2kC: 'en', W7SJWd: 'es', RrLTjT: 'de' };
  const text = {
    en: { pending: 'Saving your number…', saved: 'Your number has been saved. No message has been sent.', error: 'Your email is saved, but we could not save your number. Please try again.', retry: 'Try again', missing: 'Please reopen the form and submit your email before adding your number.' },
    es: { pending: 'Guardando tu número…', saved: 'Tu número se ha guardado. No se ha enviado ningún mensaje.', error: 'Tu correo se ha guardado, pero no hemos podido guardar tu número. Inténtalo de nuevo.', retry: 'Reintentar', missing: 'Vuelve a abrir el formulario y registra tu correo antes de añadir tu número.' },
    de: { pending: 'Deine Nummer wird gespeichert…', saved: 'Deine Nummer wurde gespeichert. Es wurde keine Nachricht versendet.', error: 'Deine E-Mail-Adresse ist gespeichert, deine Nummer konnte jedoch nicht gespeichert werden. Bitte versuche es erneut.', retry: 'Erneut versuchen', missing: 'Öffne das Formular erneut und gib deine E-Mail-Adresse ein, bevor du deine Nummer hinzufügst.' }
  };
  const emails = new Map();
  const requests = new Map();
  const show = (formId, state, retry) => {
    const copy = text[locales[formId]];
    document.querySelectorAll(`.klaviyo-form-${formId}`).forEach((embed) => {
      let status = embed.parentElement.querySelector('[data-community-capture-status]');
      if (!status) {
        status = document.createElement('div');
        status.dataset.communityCaptureStatus = '';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.style.cssText = 'padding:12px 0;font:14px/1.6 Archivo,Arial,sans-serif;color:#2d3a2e';
        embed.after(status);
      }
      status.replaceChildren(document.createTextNode(copy[state]));
      if (retry) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = copy.retry;
        button.style.cssText = 'display:block;margin-top:8px;padding:10px 16px;border:1px solid #2d3a2e;background:#ece5d6;color:#2d3a2e;font:inherit;cursor:pointer';
        button.addEventListener('click', retry, { once: true });
        status.append(button);
      }
    });
  };
  const save = async (formId, payload, key) => {
    if (requests.get(key) === 'pending' || requests.get(key) === 'saved') return;
    requests.set(key, 'pending');
    show(formId, 'pending');
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch('https://a.klaviyo.com/client/profiles/?company_id=UjFH4A', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', revision: '2026-07-15' },
        body: JSON.stringify(payload),
        signal: controller.signal,
        credentials: 'omit',
        referrerPolicy: 'strict-origin'
      });
      if (!response.ok) throw new Error('Profile capture failed');
      requests.set(key, 'saved');
      show(formId, 'saved');
    } catch {
      requests.delete(key);
      show(formId, 'error', () => save(formId, payload, key));
    } finally {
      window.clearTimeout(timeout);
    }
  };
  window.addEventListener('klaviyoForms', (event) => {
    const { formId, type, metaData = {} } = event.detail || {};
    if (!locales[formId] || !['submit', 'stepSubmit'].includes(type)) return;
    const email = metaData.email || metaData.$email;
    if (typeof email === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) emails.set(formId, email);
    if (type !== 'stepSubmit') return;
    const raw = metaData.adepsia_whatsapp_phone || metaData.phone_number || metaData.$phone_number;
    if (typeof raw !== 'string' || !raw.trim()) return;
    const number = raw.trim().replace(/[\s().-]/g, '').replace(/^00/, '+');
    const identity = emails.get(formId);
    if (!identity) return show(formId, 'missing');
    if (!/^\+[1-9]\d{7,14}$/.test(number)) return show(formId, 'error');
    const choice = metaData.adepsia_whatsapp_optin;
    const consent = Array.isArray(choice) ? choice.includes('oui') : choice === 'oui';
    const properties = {
      adepsia_whatsapp_phone: number,
      adepsia_whatsapp_optin: consent ? ['oui'] : [],
      adepsia_phone_capture_form_id: formId,
      adepsia_phone_capture_language: locales[formId],
      adepsia_phone_capture_at: new Date().toISOString()
    };
    const payload = { data: { type: 'profile', attributes: { email: identity, properties } } };
    const key = JSON.stringify([formId, identity, number, consent]);
    save(formId, payload, key);
  });
})();
